import { TEXT_FORMAT_IDS, type TextFormatId } from '../core';
import { PRO_FORMAT_IDS, type ProFormatId } from '../core/formats/pro';

/** A convertible target id — any free or Pro format the converter can produce. */
export type TargetId = TextFormatId | ProFormatId;

const KNOWN_IDS = new Set<string>([...TEXT_FORMAT_IDS, ...PRO_FORMAT_IDS]);

/** Common file-extension/legacy aliases we accept in deep links. */
const ALIASES: Record<string, TargetId> = {
  dfxp: 'ttml',
  ssa: 'ass',
  'ebu-tt-d': 'ebuttd',
  'ebutt-d': 'ebuttd',
  cea708: 'mcc',
  '708': 'mcc',
};

/** Lowercase + de-alias a raw param value; returns a known id or undefined. */
function normalizeId(raw: string | null): TargetId | undefined {
  if (raw == null) return undefined;
  const lower = raw.toLowerCase();
  const id = ALIASES[lower] ?? lower;
  return KNOWN_IDS.has(id) ? (id as TargetId) : undefined;
}

/**
 * Parse a `?from=&to=` format-pair deep link (e.g. `/app/?from=srt&to=vtt`) so a
 * marketing page can open the converter with a pair preselected. Only `to` is
 * actionable (the converter's target); `from` is a hint, since the source format
 * is detected from a loaded file and a deep link arrives with no file. Unknown or
 * missing ids are silently omitted (never thrown), so junk querystrings yield {}.
 */
export function parseConvertParams(search: string): { from?: TargetId; to?: TargetId } {
  const params = new URLSearchParams(search);
  const result: { from?: TargetId; to?: TargetId } = {};
  const from = normalizeId(params.get('from'));
  const to = normalizeId(params.get('to'));
  if (from) result.from = from;
  if (to) result.to = to;
  return result;
}
